import {AvatarStack, Box, IssueLabelToken, Octicon} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CircleIcon, CommentIcon, IssueClosedIcon, IssueOpenedIcon, PersonIcon} from '@primer/octicons-react'
import {ListItem, ListView} from '@github-ui/list-view'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {SubIssuesListQuery as SubIssuesListQueryType} from './__generated__/SubIssuesListQuery.graphql'

export const SubIssuesListQuery = graphql`
  query SubIssuesListQuery($issueID: ID!) {
    node(id: $issueID) {
      ... on Issue {
        subIssues(first: 50) {
          nodes {
            id
            title
            titleHTML
            url
            state
            labels(first: 10) {
              nodes {
                id
                name
                color
              }
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            totalCommentsCount
          }
        }
      }
    }
  }
`

export function SubIssuesList({issueID}: {issueID: string}) {
  const data = useLazyLoadQuery<SubIssuesListQueryType>(SubIssuesListQuery, {issueID})
  if (!data.node?.subIssues) return null
  return (
    <Box
      sx={{
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'border.default',
        borderRadius: 2,
      }}
    >
      <ListView
        variant="compact"
        title="Sub-issues"
        metadata={
          <ListView.Metadata
            density="condensed"
            title="Sub-issues"
            sx={{background: 'none', height: 40, borderBottom: 0}}
          />
        }
      >
        {data.node.subIssues.nodes?.map(issueNode => {
          if (!issueNode) return null
          return (
            <ListItem
              key={issueNode.id}
              title={<ListItem.Title href={issueNode.url} value={issueNode.titleHTML ?? issueNode.title} />}
              metadata={
                <ListItem.Metadata sx={{display: 'flex', gap: 2}}>
                  {issueNode.labels?.nodes?.map(label => {
                    if (!label) return null
                    return <IssueLabelToken key={label.id} fillColor={`#${label.color}`} text={label.name} />
                  })}
                  <Box sx={{width: 36, display: 'flex', alignItems: 'center', gap: 1}}>
                    {issueNode.totalCommentsCount && issueNode.totalCommentsCount > 0 ? (
                      <>
                        <CommentIcon />
                        {issueNode.totalCommentsCount}
                      </>
                    ) : null}
                  </Box>
                  <Box sx={{width: 40, display: 'flex', justifyContent: 'flex-end'}}>
                    {issueNode.assignees.totalCount === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 100,
                          position: 'relative',
                          mr: '-2px',
                        }}
                      >
                        <Octicon icon={PersonIcon} sx={{position: 'absolute', color: 'border.muted'}} />
                        <Octicon size={24} icon={CircleIcon} sx={{color: 'border.muted'}} />
                      </Box>
                    ) : (
                      <AvatarStack alignRight>
                        {issueNode.assignees.edges?.map(assigneeEdge => {
                          if (!assigneeEdge?.node) return null
                          return (
                            <GitHubAvatar
                              key={assigneeEdge.node.id}
                              alt={assigneeEdge.node.login}
                              src={assigneeEdge.node.avatarUrl}
                            />
                          )
                        })}
                      </AvatarStack>
                    )}
                  </Box>
                </ListItem.Metadata>
              }
            >
              <ListItem.LeadingContent>
                <ListItem.LeadingVisual
                  icon={issueNode.state === 'CLOSED' ? IssueClosedIcon : IssueOpenedIcon}
                  color={issueNode.state === 'CLOSED' ? 'done.fg' : 'open.fg'}
                />
              </ListItem.LeadingContent>
            </ListItem>
          )
        })}
      </ListView>
    </Box>
  )
}

try{ SubIssuesList.displayName ||= 'SubIssuesList' } catch {}